import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import PlaylistAddCheckRoundedIcon from "@mui/icons-material/PlaylistAddCheckRounded";
import * as XLSX from "xlsx";
import { fetchData } from "../../api/client";
import {
  cityCountByDealer,
  concessionExampleAoa,
  CONCESSION_EXAMPLE_FILE_NAME,
  mergeDealerMap,
  mergeDistrictSuggestions,
  parseConcessionRows,
  suggestDealerMap,
  suggestDistrictMap,
  targetsByRegionFromRanges,
  uniqueDealers,
  uniqueDistricts,
} from "./concessionSheet";
import { loadConcessionMaps, saveConcessionMaps } from "./concessionMapStorage";
import { concessionDiffToCsv, diffConcession } from "./concessionDiff";
import { buildConcessionCepExport, downloadTextFile } from "./concessionCepExport";
import { buildConcessionCepApply } from "./concessionCepApply";
import { padCep8 } from "./cepRangeAudit";
import { uid } from "./utils";

const PENDING_DISTRICT_CAP = 24;
const EXPORT_LIST_CAP = 12;

function mergeTargetsByRegion(cepRanges, targetSlugsByRegion, fetchedSlugs) {
  const fromRanges = targetsByRegionFromRanges(cepRanges);
  const out = { ...fromRanges };
  for (const src of [targetSlugsByRegion, fetchedSlugs]) {
    for (const [k, list] of Object.entries(src || {})) {
      if (!Array.isArray(list)) continue;
      const cur = out[k] || [];
      out[k] = [...new Set([...cur, ...list.map((s) => String(s || "").trim()).filter(Boolean)])].sort(
        (a, b) => a.localeCompare(b, "en")
      );
    }
  }
  return out;
}

function parseWorkbook(buffer) {
  const wb = XLSX.read(buffer, { type: "array" });
  const name = wb.SheetNames[0];
  const aoa = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: "", raw: false });
  return parseConcessionRows(aoa);
}

function unwrapPayload(data) {
  if (data?.byRegion || data?.ranges || data?.points) return data;
  if (data?.data && !Array.isArray(data.data)) return data.data;
  return data || {};
}

function CompactSelect({ label, value, onChange, disabled, children, sx, error }) {
  return (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={onChange}
      disabled={disabled}
      error={error}
      SelectProps={{ native: true }}
      sx={{ minWidth: 220, flex: 1, ...sx }}
    >
      {children}
    </TextField>
  );
}

function SoftAccordion({ expanded, onChange, summary, children }) {
  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={onChange}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        "&:before": { display: "none" },
        bgcolor: "action.hover",
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2" color="text.secondary">
          {summary}
        </Typography>
      </AccordionSummary>
      {expanded ? <AccordionDetails sx={{ pt: 0 }}>{children}</AccordionDetails> : null}
    </Accordion>
  );
}

export default function ConcessionDiffDialog({
  open,
  onClose,
  regionKeys = [],
  cepRanges = [],
  targetSlugsByRegion,
  onTargetSlugsByRegion,
  onApplyRanges,
}) {
  const fileRef = useRef(null);
  const fetchedRef = useRef(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState("");
  const [sheetRows, setSheetRows] = useState([]);
  const [dealerMap, setDealerMap] = useState({});
  const [districtMap, setDistrictMap] = useState({});
  const [skippedDealers, setSkippedDealers] = useState([]);
  const [skippedDistricts, setSkippedDistricts] = useState([]);
  const [fetchedSlugs, setFetchedSlugs] = useState({});
  const [unavailableRegions, setUnavailableRegions] = useState({});
  const [loadingRegions, setLoadingRegions] = useState([]);
  const [showMappedDealers, setShowMappedDealers] = useState(false);
  const [showMappedDistricts, setShowMappedDistricts] = useState(false);
  const [openDistrictBrand, setOpenDistrictBrand] = useState("");
  const [showAllPendingDistricts, setShowAllPendingDistricts] = useState(false);
  const [showExportDetails, setShowExportDetails] = useState(false);
  const [showAllExports, setShowAllExports] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyPack, setApplyPack] = useState(null);
  const [diff, setDiff] = useState(null);
  const [exportPack, setExportPack] = useState(null);
  const [tab, setTab] = useState("brand");
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const persistReadyRef = useRef(false);

  const targetsByRegion = useMemo(
    () => mergeTargetsByRegion(cepRanges, targetSlugsByRegion, fetchedSlugs),
    [cepRanges, targetSlugsByRegion, fetchedSlugs]
  );
  const dealers = useMemo(() => uniqueDealers(sheetRows), [sheetRows]);
  const citiesByDealer = useMemo(() => cityCountByDealer(sheetRows), [sheetRows]);

  const resetResults = () => {
    setDiff(null);
    setPage(0);
    setExportPack(null);
    setApplyPack(null);
    setApplyOpen(false);
  };

  const ensureRegionTargets = useCallback(
    async (regions) => {
      const need = [...new Set((regions || []).filter(Boolean))].filter((region) => {
        if (fetchedRef.current.has(region)) return false;
        if (Array.isArray(targetSlugsByRegion?.[region])) {
          fetchedRef.current.add(region);
          return false;
        }
        return true;
      });
      if (!need.length) return;
      need.forEach((region) => fetchedRef.current.add(region));
      setLoadingRegions((prev) => [...new Set([...prev, ...need])]);
      try {
        const data = await fetchData(
          `/companies/cep-region-targets?regions=${need.map(encodeURIComponent).join(",")}`,
          "GET",
          null,
          { timeout: 60000 }
        );
        const payload = unwrapPayload(data);
        const byRegion = payload?.byRegion || {};
        const incoming = {};
        const unavailable = {};
        for (const region of need) {
          const entry = byRegion[region] || {};
          incoming[region] = Array.isArray(entry.slugs) ? entry.slugs : [];
          if (entry.unavailable) unavailable[region] = true;
        }
        setFetchedSlugs((prev) => ({ ...prev, ...incoming }));
        setUnavailableRegions((prev) => ({ ...prev, ...unavailable }));
        onTargetSlugsByRegion?.({ ...(targetSlugsByRegion || {}), ...incoming });
      } catch (err) {
        need.forEach((region) => fetchedRef.current.delete(region));
        setError(err?.message || "Não foi possível carregar as empresas (projetos) do workspace.");
      } finally {
        setLoadingRegions((prev) => prev.filter((region) => !need.includes(region)));
      }
    },
    [onTargetSlugsByRegion, targetSlugsByRegion]
  );

  useEffect(() => {
    if (!open || !sheetRows.length) return;
    const regions = [...new Set(Object.values(dealerMap).filter(Boolean))];
    ensureRegionTargets(regions);
  }, [open, dealerMap, sheetRows.length, ensureRegionTargets]);

  useEffect(() => {
    if (!sheetRows.length) return;
    setDistrictMap((prev) => {
      const suggested = suggestDistrictMap(sheetRows, dealerMap, targetsByRegion);
      const next = mergeDistrictSuggestions(prev, suggested, skippedDistricts);
      const same =
        Object.keys(next).length === Object.keys(prev).length &&
        Object.entries(next).every(([k, v]) => prev[k] === v);
      return same ? prev : next;
    });
  }, [sheetRows, dealerMap, targetsByRegion, skippedDistricts]);

  useEffect(() => {
    if (!persistReadyRef.current) return;
    saveConcessionMaps({ dealerMap, districtMap, skippedDealers, skippedDistricts });
  }, [dealerMap, districtMap, skippedDealers, skippedDistricts]);

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    resetResults();
    setShowMappedDealers(false);
    setShowMappedDistricts(false);
    setOpenDistrictBrand("");
    setShowAllPendingDistricts(false);
    try {
      const buf = await file.arrayBuffer();
      const parsed = parseWorkbook(buf);
      if (parsed.errors.length) {
        setSheetRows([]);
        setError(parsed.errors.join(" "));
        return;
      }
      setFileName(file.name);
      setSheetRows(parsed.rows);
      const saved = loadConcessionMaps();
      const dMap = mergeDealerMap(
        suggestDealerMap(parsed.rows, regionKeys),
        { ...saved.dealerMap, ...dealerMap },
        [...new Set([...(saved.skippedDealers || []), ...skippedDealers])]
      );
      const skipDistricts = [...new Set([...(saved.skippedDistricts || []), ...skippedDistricts])];
      const distMap = mergeDistrictSuggestions(
        { ...saved.districtMap, ...districtMap },
        suggestDistrictMap(parsed.rows, dMap, targetsByRegion),
        skipDistricts
      );
      const nextSkippedDealers = [...new Set([...(saved.skippedDealers || []), ...skippedDealers])];
      setSkippedDealers(nextSkippedDealers);
      setSkippedDistricts(skipDistricts);
      setDealerMap(dMap);
      setDistrictMap(distMap);
      persistReadyRef.current = true;
      saveConcessionMaps({
        dealerMap: dMap,
        districtMap: distMap,
        skippedDealers: nextSkippedDealers,
        skippedDistricts: skipDistricts,
      });
    } catch (err) {
      setSheetRows([]);
      setError(err?.message || "Não foi possível ler a planilha.");
    } finally {
      setBusy(false);
    }
  };

  const setDealer = (dealer, region) => {
    const districts = uniqueDistricts(sheetRows, dealer);
    persistReadyRef.current = true;
    setSkippedDealers((prev) => {
      const next = new Set(prev);
      if (region) next.delete(dealer);
      else next.add(dealer);
      return [...next];
    });
    setSkippedDistricts((prev) => prev.filter((d) => !districts.includes(d)));
    setDealerMap((prev) => ({ ...prev, [dealer]: region }));
    setDistrictMap((prev) => {
      const next = { ...prev };
      districts.forEach((d) => {
        next[d] = "";
      });
      return next;
    });
    resetResults();
    if (region) ensureRegionTargets([region]);
  };

  const setDistrict = (district, target) => {
    persistReadyRef.current = true;
    setSkippedDistricts((prev) => {
      const next = new Set(prev);
      if (target) next.delete(district);
      else next.add(district);
      return [...next];
    });
    setDistrictMap((prev) => ({ ...prev, [district]: target }));
    resetResults();
  };

  const loadMunRanges = async () => {
    const data = await fetchData("/cep/municipality-ranges", "GET", null, { timeout: 60000 });
    const payload = unwrapPayload(data);
    return payload?.ranges || [];
  };

  const buildExportPack = async () => {
    const ranges = await loadMunRanges();
    return buildConcessionCepExport({
      sheetRows,
      dealerMap,
      districtMap,
      municipalityRanges: ranges,
    });
  };

  const generateExport = async () => {
    setBusy(true);
    setError(null);
    try {
      setExportPack(await buildExportPack());
    } catch (err) {
      setError(err?.message || "Falha ao gerar as faixas de CEP das concessões.");
    } finally {
      setBusy(false);
    }
  };

  const prepareApply = async () => {
    setBusy(true);
    setError(null);
    try {
      const ranges = await loadMunRanges();
      const pack = buildConcessionCepApply({
        sheetRows,
        dealerMap,
        districtMap,
        municipalityRanges: ranges,
        existingRanges: cepRanges,
        regionKeys,
        targetSlugsByRegion: targetsByRegion,
      });
      setApplyPack(pack);
      setApplyOpen(true);
    } catch (err) {
      setError(err?.message || "Falha ao montar as faixas da planilha sem sobreposição.");
    } finally {
      setBusy(false);
    }
  };

  const confirmApply = () => {
    if (!applyPack?.canApply || !onApplyRanges) return;
    onApplyRanges(
      applyPack.nextRanges.map((r) => ({
        ...r,
        _id: r._id || uid(),
      }))
    );
    setApplyOpen(false);
  };

  const runCompare = async () => {
    setBusy(true);
    setError(null);
    try {
      const [data, pack] = await Promise.all([
        fetchData(
          "/companies/cep-coverage",
          "POST",
          {
            cepRanges: (cepRanges || []).map((r, idx) => ({
              id: r.id,
              region_key: r.region_key,
              start_cep: r.start_cep,
              end_cep: r.end_cep,
              target_cod_interno: r.target_cod_interno,
              status: r.status,
              ordem: r.ordem != null ? r.ordem : idx,
            })),
          },
          { timeout: 60000 }
        ),
        buildExportPack(),
      ]);
      const report = unwrapPayload(data);
      const result = diffConcession({
        sheetRows,
        coveragePoints: report?.points || [],
        dealerMap,
        districtMap,
      });
      setDiff(result);
      setExportPack(pack);
      setTab(result.summary.brandMismatch ? "brand" : result.summary.missingCoverage ? "missing" : "branch");
      setPage(0);
    } catch (err) {
      setError(err?.message || "Falha ao comparar com a cerca virtual.");
    } finally {
      setBusy(false);
    }
  };

  const rowsForTab = useMemo(() => {
    if (!diff) return [];
    const list =
      tab === "brand"
        ? diff.brandMismatch
        : tab === "branch"
          ? diff.branchMismatch
          : tab === "missing"
            ? diff.missingCoverage
            : diff.extraCoverage;
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((r) =>
      [r.city, r.uf, r.ibge, r.dealer, r.district, r.expectedRegion, r.expectedTarget, r.actualRegion, r.actualTarget]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle))
    );
  }, [diff, tab, q]);

  const pageRows = rowsForTab.slice(page * 10, page * 10 + 10);
  const unmappedDealers = dealers.filter((d) => !dealerMap[d]);
  const mappedDealers = dealers.filter((d) => dealerMap[d]);

  const districtGroups = useMemo(() => {
    return mappedDealers.map((dealer) => {
      const region = dealerMap[dealer];
      const districts = uniqueDistricts(sheetRows, dealer);
      const pending = districts.filter((dist) => !districtMap[dist]);
      const done = districts.filter((dist) => districtMap[dist]);
      return {
        dealer,
        region,
        districts,
        pending,
        done,
        loading: loadingRegions.includes(region),
        unavailable: Boolean(unavailableRegions[region]),
        targets: targetsByRegion[region] || [],
      };
    });
  }, [
    mappedDealers,
    dealerMap,
    sheetRows,
    districtMap,
    loadingRegions,
    unavailableRegions,
    targetsByRegion,
  ]);

  const pendingDistrictCount = districtGroups.reduce((n, g) => n + g.pending.length, 0);
  const mappedDistrictCount = districtGroups.reduce((n, g) => n + g.done.length, 0);

  const downloadExampleSheet = () => {
    const ws = XLSX.utils.aoa_to_sheet(concessionExampleAoa());
    ws["!cols"] = [{ wch: 10 }, { wch: 36 }, { wch: 18 }, { wch: 28 }, { wch: 16 }, { wch: 22 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Concessões");
    XLSX.writeFile(wb, CONCESSION_EXAMPLE_FILE_NAME);
  };

  const downloadCsv = () => {
    if (!diff) return;
    const blob = new Blob(["\ufeff" + concessionDiffToCsv(diff)], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "divergencias-concessoes.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const readyToAnalyze = mappedDealers.length > 0;
  const pendingCityCount = unmappedDealers.reduce((n, d) => n + (citiesByDealer[d] || 0), 0);

  const workspaceOptions = (
    <>
      <option value="">Não incluir na análise</option>
      {regionKeys.map((k) => (
        <option key={k} value={k}>
          {k}
        </option>
      ))}
    </>
  );

  const renderDealerRow = (dealer, { pending } = {}) => (
    <Stack
      key={dealer}
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      alignItems={{ sm: "center" }}
    >
      <Box sx={{ minWidth: 220 }}>
        <Typography variant="body2" fontWeight={pending ? 700 : 400}>
          {dealer}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {citiesByDealer[dealer] || 0} município(s)
        </Typography>
      </Box>
      <CompactSelect
        label="Workspace"
        value={dealerMap[dealer] || ""}
        onChange={(e) => setDealer(dealer, e.target.value)}
        error={pending && !dealerMap[dealer]}
      >
        {workspaceOptions}
      </CompactSelect>
    </Stack>
  );

  const renderDistrictRow = (dealer, dist, targets, loading) => (
    <Stack
      key={dist}
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      alignItems={{ sm: "center" }}
      sx={{ py: 0.25 }}
    >
      <Typography variant="body2" sx={{ minWidth: 260 }}>
        {dist}
      </Typography>
      <CompactSelect
        label="Empresa (projeto)"
        value={districtMap[dist] || ""}
        onChange={(e) => setDistrict(dist, e.target.value)}
        disabled={loading || !targets.length}
      >
        <option value="">{loading ? "Carregando empresas…" : "Só conferir a marca"}</option>
        {targets.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </CompactSelect>
    </Stack>
  );

  const exportDealers = exportPack?.dealers || [];
  const exportDistricts = exportPack?.districts || [];
  const visibleExportDealers = showAllExports ? exportDealers : exportDealers.slice(0, EXPORT_LIST_CAP);
  const visibleExportDistricts = showAllExports ? exportDistricts : exportDistricts.slice(0, EXPORT_LIST_CAP);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        Comparar planilha de concessões
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Municípios entram pelo código IBGE — a planilha não precisa ter CEP. Associe o que falta,
          inicie a análise e aplique as faixas no cadastro (sem sobreposição) ou baixe o CSV. As
          associações marca→workspace e filial→empresa ficam salvas neste workspace.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {busy ? <LinearProgress /> : null}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <input
              ref={fileRef}
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                onFile(f);
              }}
            />
            <Button
              variant={sheetRows.length ? "outlined" : "contained"}
              startIcon={<UploadFileRoundedIcon />}
              onClick={() => fileRef.current?.click()}
              disabled={busy}
            >
              {sheetRows.length ? "Trocar planilha" : "Enviar planilha"}
            </Button>
            <Button
              variant="text"
              startIcon={<DownloadRoundedIcon />}
              onClick={downloadExampleSheet}
              disabled={busy}
            >
              Baixar exemplo
            </Button>
            {fileName ? (
              <Typography variant="body2" color="text.secondary" noWrap>
                {fileName} · {sheetRows.length} município(s)
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Colunas: ID (IBGE), município, marca e filial
              </Typography>
            )}
          </Stack>
          {error ? <Alert severity="error">{error}</Alert> : null}

          {!sheetRows.length ? (
            <Alert severity="info">
              Envie a planilha para ver o que falta associar. Se precisar, baixe o exemplo com as
              colunas esperadas (código IBGE, município, marca e filial — sem CEP).
            </Alert>
          ) : (
            <>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Para iniciar a análise
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" color="success" label={`Planilha · ${sheetRows.length} municípios`} />
                  <Chip
                    size="small"
                    color={unmappedDealers.length ? "warning" : "success"}
                    label={
                      unmappedDealers.length
                        ? `Falta workspace em ${unmappedDealers.length} marca(s)`
                        : `${mappedDealers.length} marca(s) associadas`
                    }
                  />
                  <Chip
                    size="small"
                    color={pendingDistrictCount ? "default" : "success"}
                    variant={pendingDistrictCount ? "outlined" : "filled"}
                    label={
                      pendingDistrictCount
                        ? `${pendingDistrictCount} filial(is) sem empresa`
                        : `${mappedDistrictCount} filial(is) associadas`
                    }
                  />
                </Stack>
                {unmappedDealers.length ? (
                  <Typography variant="body2" sx={{ mt: 1.25 }}>
                    Associe o workspace das marcas abaixo. Sem isso,{" "}
                    <strong>
                      {unmappedDealers.join(", ")} ({pendingCityCount} município
                      {pendingCityCount === 1 ? "" : "s"})
                    </strong>{" "}
                    fica de fora. O restante já pode ser analisado.
                  </Typography>
                ) : pendingDistrictCount ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
                    As marcas já estão ligadas. Falta escolher a empresa (projeto) das filiais para
                    comparar o destino — ou inicie a análise só pela marca.
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
                    Pronto: marcas e filiais associadas. Inicie a análise.
                  </Typography>
                )}
              </Paper>

              {unmappedDealers.length ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderColor: "warning.main",
                    bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
                  }}
                >
                  <Typography variant="subtitle2">Falta associar marca → workspace</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Escolha o workspace deste cadastro. Em seguida carregamos as empresas (projetos)
                    para você ligar as filiais.
                  </Typography>
                  <Stack spacing={1.25}>{unmappedDealers.map((d) => renderDealerRow(d, { pending: true }))}</Stack>
                </Paper>
              ) : null}

              {mappedDealers.length ? (
                <SoftAccordion
                  expanded={showMappedDealers}
                  onChange={(_, exp) => setShowMappedDealers(exp)}
                  summary={`Marcas já associadas (${mappedDealers.length}) — ver e ajustar`}
                >
                  <Stack spacing={1}>{mappedDealers.map((d) => renderDealerRow(d))}</Stack>
                </SoftAccordion>
              ) : null}

              {pendingDistrictCount ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderColor: "info.main",
                    bgcolor: (t) => alpha(t.palette.info.main, 0.06),
                  }}
                >
                  <Typography variant="subtitle2">Falta associar filial → empresa (projeto)</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    As opções vêm do workspace da marca. Sem empresa, a análise compara só a
                    concessionária.
                  </Typography>
                  {districtGroups
                    .filter((g) => g.pending.length || g.loading || g.unavailable)
                    .map((g) => (
                      <Box key={g.dealer} sx={{ mb: 1.5 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="caption" fontWeight={700}>
                            {g.dealer} → {g.region}
                          </Typography>
                          {g.loading ? <CircularProgress size={14} /> : null}
                        </Stack>
                        {g.unavailable ? (
                          <Alert severity="warning" sx={{ mb: 1 }}>
                            Não foi possível ler as empresas de {g.region}.
                          </Alert>
                        ) : null}
                        {!g.loading && !g.unavailable && !g.targets.length ? (
                          <Typography variant="caption" color="text.secondary">
                            Nenhuma empresa (projeto) neste workspace.
                          </Typography>
                        ) : null}
                        {(showAllPendingDistricts
                          ? g.pending
                          : g.pending.slice(0, PENDING_DISTRICT_CAP)
                        ).map((dist) => renderDistrictRow(g.dealer, dist, g.targets, g.loading))}
                        {!showAllPendingDistricts && g.pending.length > PENDING_DISTRICT_CAP ? (
                          <Button size="small" onClick={() => setShowAllPendingDistricts(true)}>
                            Mostrar as {g.pending.length - PENDING_DISTRICT_CAP} filiais restantes desta marca
                          </Button>
                        ) : null}
                      </Box>
                    ))}
                </Paper>
              ) : null}

              {mappedDistrictCount ? (
                <SoftAccordion
                  expanded={showMappedDistricts}
                  onChange={(_, exp) => {
                    setShowMappedDistricts(exp);
                    if (exp && !openDistrictBrand) {
                      setOpenDistrictBrand(districtGroups.find((g) => g.done.length)?.dealer || "");
                    }
                  }}
                  summary={`Filiais já associadas (${mappedDistrictCount}) — ver e ajustar`}
                >
                  <Stack spacing={0.5}>
                    {districtGroups
                      .filter((g) => g.done.length)
                      .map((g) => {
                        const open = openDistrictBrand === g.dealer;
                        return (
                          <Box key={g.dealer}>
                            <Button
                              size="small"
                              color="inherit"
                              onClick={() => setOpenDistrictBrand(open ? "" : g.dealer)}
                              sx={{ textTransform: "none", fontWeight: 600 }}
                            >
                              {open ? "▾" : "▸"} {g.dealer} → {g.region} ({g.done.length})
                            </Button>
                            {open
                              ? g.done.map((dist) => renderDistrictRow(g.dealer, dist, g.targets, g.loading))
                              : null}
                          </Box>
                        );
                      })}
                  </Stack>
                </SoftAccordion>
              ) : null}

              <Alert severity={readyToAnalyze ? (unmappedDealers.length ? "info" : "success") : "warning"}>
                {readyToAnalyze
                  ? unmappedDealers.length
                    ? `Pode analisar ${mappedDealers.length} de ${dealers.length} marcas. ${unmappedDealers.join(", ")} fica de fora.`
                    : pendingDistrictCount
                      ? `Marcas prontas. ${pendingDistrictCount} filial(is) ainda sem empresa — o destino dessas cidades não entra na comparação.`
                      : "Tudo associado. Pode iniciar a análise."
                  : "Associe pelo menos uma marca a um workspace para iniciar."}
              </Alert>
            </>
          )}

          {exportPack ? (
            <Box>
              <Typography variant="subtitle2">Faixas para importar (CEP Inicial;CEP Final)</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Cidades da planilha viram faixas municipais de CEP; intervalos vizinhos são unidos.{" "}
                {exportPack.matchedCities} município(s) com CEP
                {exportPack.missingIbge ? ` · ${exportPack.missingIbge} sem faixa IBGE` : ""}.{" "}
                Preferível aplicar no cadastro: usa a filial como destino e recorta cruzamentos,
                em vez de importar um CSV por marca.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                {onApplyRanges && diff ? (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<PlaylistAddCheckRoundedIcon />}
                    onClick={prepareApply}
                    disabled={busy}
                  >
                    Aplicar faixas no cadastro
                  </Button>
                ) : null}
                <Button
                  size="small"
                  variant={onApplyRanges && diff ? "outlined" : "contained"}
                  startIcon={<DownloadRoundedIcon />}
                  onClick={async () => {
                    for (const item of exportPack.dealers) {
                      downloadTextFile(item.fileName, item.csv);
                      await new Promise((r) => setTimeout(r, 200));
                    }
                  }}
                >
                  Baixar todas as concessionárias ({exportPack.dealers.length})
                </Button>
                <Button size="small" variant="text" onClick={() => setShowExportDetails((v) => !v)}>
                  {showExportDetails ? "Ocultar arquivos" : "Ver arquivos individuais"}
                </Button>
              </Stack>
              {showExportDetails ? (
                <>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Por concessionária
                  </Typography>
                  {visibleExportDealers.map((item) => (
                    <Stack
                      key={item.dealer}
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ sm: "center" }}
                      sx={{ py: 0.25 }}
                    >
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {item.dealer}
                        {item.region ? ` → ${item.region}` : ""} · {item.cities} cidade(s) · {item.rangeCount}{" "}
                        faixa(s)
                      </Typography>
                      <Button size="small" onClick={() => downloadTextFile(item.fileName, item.csv)}>
                        Baixar CSV
                      </Button>
                    </Stack>
                  ))}
                  <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mt: 1.5 }}>
                    Por filial
                  </Typography>
                  {visibleExportDistricts.map((item) => (
                    <Stack
                      key={item.district}
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ sm: "center" }}
                      sx={{ py: 0.25 }}
                    >
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {item.district}
                        {item.target ? ` → ${item.target}` : ""} · {item.cities} cidade(s) · {item.rangeCount}{" "}
                        faixa(s)
                      </Typography>
                      <Button size="small" onClick={() => downloadTextFile(item.fileName, item.csv)}>
                        Baixar CSV
                      </Button>
                    </Stack>
                  ))}
                  {!showAllExports &&
                  (exportDealers.length > EXPORT_LIST_CAP || exportDistricts.length > EXPORT_LIST_CAP) ? (
                    <Button size="small" onClick={() => setShowAllExports(true)}>
                      Mostrar todos os arquivos
                    </Button>
                  ) : null}
                </>
              ) : null}
            </Box>
          ) : null}

          {diff ? (
            <>
              <Alert severity={diff.summary.brandMismatch || diff.summary.missingCoverage ? "warning" : "success"}>
                {diff.summary.brandMismatch} cidade(s) com marca diferente · {diff.summary.branchMismatch}{" "}
                com destino diferente · {diff.summary.missingCoverage} na planilha sem faixa de CEP ·{" "}
                {diff.summary.extraCoverage} na cerca sem a planilha
                {diff.unmappedDealerNames.length
                  ? ` · marcas não associadas: ${diff.unmappedDealerNames.join(", ")}`
                  : ""}
                .
              </Alert>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {[
                  ["brand", `Marca diferente (${diff.summary.brandMismatch})`],
                  ["branch", `Destino diferente (${diff.summary.branchMismatch})`],
                  ["missing", `Sem cerca (${diff.summary.missingCoverage})`],
                  ["extra", `Cerca extra (${diff.summary.extraCoverage})`],
                ].map(([id, label]) => (
                  <Button
                    key={id}
                    size="small"
                    variant={tab === id ? "contained" : "outlined"}
                    onClick={() => {
                      setTab(id);
                      setPage(0);
                    }}
                  >
                    {label}
                  </Button>
                ))}
                <Button size="small" startIcon={<DownloadRoundedIcon />} onClick={downloadCsv}>
                  Baixar CSV
                </Button>
              </Stack>
              <TextField
                size="small"
                placeholder="Filtrar cidade, IBGE, marca…"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(0);
                }}
              />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>IBGE</TableCell>
                    <TableCell>Cidade</TableCell>
                    <TableCell>Planilha</TableCell>
                    <TableCell>Cadastro (vencedor)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((r, i) => (
                    <TableRow key={`${r.ibge}-${i}`}>
                      <TableCell>{r.ibge}</TableCell>
                      <TableCell>
                        {r.city || r.cityCadastro || "—"}
                        {r.uf ? `/${r.uf}` : ""}
                      </TableCell>
                      <TableCell>
                        {r.dealer || "—"}
                        {r.district ? ` → ${r.district}` : ""}
                      </TableCell>
                      <TableCell>
                        {tab === "missing" ? (
                          "Sem faixa de CEP no cadastro"
                        ) : (
                          <>
                            {r.actualRegion || "—"}
                            {r.actualTarget ? ` → ${r.actualTarget}` : ""}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!pageRows.length ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          Nenhuma divergência neste filtro.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={rowsForTab.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={10}
                rowsPerPageOptions={[10]}
              />
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        {sheetRows.length ? (
          <>
            <Button onClick={generateExport} disabled={busy}>
              Só gerar faixas
            </Button>
            {onApplyRanges && diff ? (
              <Button onClick={prepareApply} disabled={busy} startIcon={<PlaylistAddCheckRoundedIcon />}>
                Aplicar faixas
              </Button>
            ) : null}
            <Button variant="contained" onClick={runCompare} disabled={busy || !readyToAnalyze}>
              {diff ? "Repetir análise" : "Iniciar análise"}
            </Button>
          </>
        ) : null}
      </DialogActions>
      <Dialog open={applyOpen} onClose={() => !busy && setApplyOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Aplicar faixas da planilha</DialogTitle>
        <DialogContent dividers>
          {applyPack ? (
            <Stack spacing={1.5}>
              <Alert severity={applyPack.canApply ? "info" : "error"}>
                {applyPack.canApply
                  ? "As cidades com marca e filial associadas viram faixas municipais de CEP, sem sobreposição. O e-commerce usa first-match; com faixas disjuntas o destino fica unívoco. As faixas entram no cadastro — salve a empresa para publicar."
                  : applyPack.blockingReasons.join(" ")}
              </Alert>
              <Typography variant="body2">
                {applyPack.summary.appliedCities} município(s) entram · {applyPack.summary.newRanges}{" "}
                faixa(s) novas
                {applyPack.appliedRegions.length
                  ? ` · substituem as faixas atuais de ${applyPack.appliedRegions.join(", ")}`
                  : ""}
                {applyPack.summary.keptRanges
                  ? ` · ${applyPack.summary.keptRanges} faixa(s) de marcas de fora permanecem`
                  : ""}
                {applyPack.summary.carvedExisting
                  ? ` (${applyPack.summary.carvedExisting} recortada(s) para não cruzar)`
                  : ""}
                .
              </Typography>
              {applyPack.summary.skipNoRegion ||
              applyPack.summary.skipNoTarget ||
              applyPack.summary.skipNoIbge ? (
                <Alert severity="warning">
                  Fora desta aplicação
                  {applyPack.skipped.noRegion.length
                    ? `: sem workspace ${applyPack.skipped.noRegion.join(", ")}`
                    : ""}
                  {applyPack.summary.skipNoTarget
                    ? `${applyPack.skipped.noRegion.length ? " ·" : ":"} ${applyPack.summary.skipNoTarget} município(s) sem empresa (filial)`
                    : ""}
                  {applyPack.summary.skipNoIbge
                    ? ` · ${applyPack.summary.skipNoIbge} sem faixa IBGE de CEP`
                    : ""}
                  .
                </Alert>
              ) : null}
              {applyPack.conflicts.length ? (
                <Alert severity="warning">
                  {applyPack.conflicts.length} intervalo(s) de CEP municipal cruzavam destinos
                  diferentes e foram recortados. Vence a faixa IBGE menor (mais específica).
                  <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                    {applyPack.conflicts.slice(0, 8).map((c) => (
                      <li key={`${c.start}-${c.end}-${c.winner.region}-${c.winner.target}`}>
                        <Typography variant="caption" component="span">
                          {padCep8(c.start)}–{padCep8(c.end)} → {c.winner.region} / {c.winner.target}
                          {c.winner.city ? ` (${c.winner.city})` : ""}
                          {c.losers?.[0]
                            ? `, em vez de ${c.losers[0].region} / ${c.losers[0].target}`
                            : ""}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                  {applyPack.conflicts.length > 8 ? (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      + {applyPack.conflicts.length - 8} intervalo(s)
                    </Typography>
                  ) : null}
                </Alert>
              ) : null}
              <Typography variant="caption" color="text.secondary">
                Auditoria: {applyPack.audit.summary.blocking} bloqueio(s), {applyPack.audit.summary.warn}{" "}
                aviso(s). O save da empresa recusa o mesmo tipo de sobreposição.
              </Typography>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={confirmApply}
            disabled={!applyPack?.canApply}
            startIcon={<PlaylistAddCheckRoundedIcon />}
          >
            Aplicar no cadastro
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
